import { useEffect, useRef, useState } from 'react';

import { Estocagem } from './telas/Estocagem.jsx';
import { Laboratorio } from './telas/Laboratorio.jsx';
import { LaboratorioConferir } from './telas/LaboratorioConferir.jsx';
import { LaboratorioLaudo } from './telas/LaboratorioLaudo.jsx';
import { LaboratorioPrever } from './telas/LaboratorioPrever.jsx';
import { Simulador } from './telas/Simulador.jsx';
import { Substrato } from './telas/Substrato.jsx';
import { Substratos } from './telas/Substratos.jsx';
import { TELA_INICIAL, paraRota, useRolarAoTrocar, useRota } from './nucleo/rota.js';
import { aplicarTema, temaEscolhido, proximoTema } from './nucleo/tema.js';
import { conferirVersao } from './nucleo/armazenamento.js';

/**
 * O esqueleto: cabeçalho, navegação, a tela da vez, e o rodapé dela.
 *
 * O roteador nasceu com a segunda tela, como estava escrito aqui que
 * nasceria. Ele é de hash (`#/substratos`) e os três motivos estão em
 * `nucleo/rota.js` — o principal é que o deploy ainda não existe, e rota de
 * caminho faria a navegação depender de um `try_files` que ninguém escreveu.
 *
 * **O rodapé é por tela, e isso não é capricho.** O texto que estava aqui
 * fala de "dejeto fresco, biodigestor em temperatura ambiente e energia
 * consumida na propriedade" — são as condições em que a *simulação* vale, e
 * não dizem nada sobre uma ficha de catálogo. Rodapé que não corresponde à
 * tela ensina a não ler rodapé, e aí o aviso que importa também não é lido.
 *
 * A ressalva não é formalidade jurídica. Uma estimativa de viabilidade que não
 * diz em que condições vale é a razão de o setor desconfiar de calculadora de
 * biogás: o produtor leva o número ao banco, o número não se sustenta, e a
 * próxima conta honesta também não é levada a sério.
 */

const NOME_DO_TEMA = {
  sistema: 'Tema: o do aparelho',
  light: 'Tema: claro',
  dark: 'Tema: escuro',
};

const SIMBOLO_DO_TEMA = { sistema: '◐', light: '☀', dark: '☾' };

/** O menu. Só entra aqui tela que existe — link para tela vazia é pior que ausência. */
const MENU = [
  { tela: 'simulador', rotulo: 'Simular' },
  { tela: 'substratos', rotulo: 'Materiais' },
  { tela: 'estocagem', rotulo: 'Estocagem' },
  { tela: 'laboratorio', rotulo: 'Laboratório' },
];

/**
 * Cada tela diz em que condições o que ela mostra vale.
 *
 * `substrato` (singular) herda o texto de `substratos` porque é a mesma
 * natureza de informação, vista de perto.
 */
const RODAPE = {
  simulador: {
    vale:
      'Estimativa de pré-viabilidade. Vale para dejeto fresco, biodigestor operando em ' +
      'temperatura ambiente e energia consumida na própria propriedade. Projeto executivo, ' +
      'dimensionamento e financiamento pedem análise do seu material e visita técnica.',
    onde: 'Cada número desta tela tem a conta aberta em "ver a conta passo a passo", com a fonte de cada premissa.',
  },
  substratos: {
    vale:
      'Os potenciais de metano aqui são de ensaio de laboratório em batelada, não de biodigestor ' +
      'em operação. Um reator de campo entrega menos: tempo de retenção, temperatura e mistura ' +
      'cobram a diferença. Serve para comparar materiais e para saber de onde vem cada número — ' +
      'não substitui análise do seu dejeto.',
    onde: 'Cada categoria abre a citação inteira, com método e tamanho de amostra, e diz qual número é medido e qual é derivado.',
  },
  laboratorio: {
    vale:
      'Nada aqui substitui o ensaio de BMP. A previsão por composição é modelo empírico ' +
      'de literatura: Ziero et al. (2026) rodaram 13 modelos sobre 10 resíduos e, das 650 ' +
      'combinações, uma foi excelente e 28 inaceitáveis. A leitura de laudo por DQO/COT é ' +
      'a exceção — ali é estequiometria, e vale exatamente porque não é correlação.',
    onde:
      'Cada modelo aparece com a fórmula, o R² publicado e o erro que o próprio autor ' +
      'declarou. A discordância entre eles é mostrada, nunca resolvida numa média.',
  },
  estocagem: {
    vale:
      'A perda por estocagem é medida em frasco, comparando o mesmo dejeto fresco e guardado. ' +
      'Nenhum destes ensaios é brasileiro: são quatro estudos europeus e um da Embrapa com cama ' +
      'de frango, em climas e manejos que não são o do leitor. A direção da perda vale em ' +
      'qualquer clima (é biologia, não geografia); o TAMANHO dela aqui é mais quente e mais ' +
      'rápido no Brasil, não mais brando.',
    onde:
      'Cada medição traz o estudo inteiro, o eixo em que foi lida e quais categorias do ' +
      'catálogo a reusam — para que 11 medições não pareçam 21.',
  },
};

function RodapeDaTela({ tela }) {
  const texto = RODAPE[tela === 'substrato' ? 'substratos' : tela] ?? RODAPE[TELA_INICIAL];
  return (
    <footer className="rodape pagina">
      <p>{texto.vale}</p>
      <p className="terciario">{texto.onde}</p>
    </footer>
  );
}

/**
 * Rota que não existe abre a tela inicial, dizendo que abriu.
 *
 * O caso real não é alguém digitando errado: é um link antigo depois de eu
 * renomear uma rota. Mandar essa pessoa para o simulador em silêncio a faria
 * concluir que a tela que ela procurava não existe mais. Uma linha resolve, e
 * a navegação continua à vista.
 */
function TelaDesconhecida({ tela }) {
  return (
    <p className="aviso-de-cache" role="status">
      Não conheço a tela "{tela}" — pode ser um link de uma versão anterior. Abri o simulador; o
      menu acima tem o que existe hoje.
    </p>
  );
}

function Conteudo({ tela, parte }) {
  if (tela === 'substratos') {
    return parte ? <Substrato chave={parte} /> : <Substratos />;
  }
  if (tela === 'estocagem') return <Estocagem />;
  if (tela === 'laboratorio') {
    if (parte === 'prever') return <LaboratorioPrever />;
    if (parte === 'conferir') return <LaboratorioConferir />;
    if (parte === 'laudo') return <LaboratorioLaudo />;
    // Parte desconhecida cai no indice em vez de cair no simulador: quem
    // chegou com /laboratorio/qualquer-coisa ainda quer laboratorio.
    return <Laboratorio />;
  }
  if (tela === TELA_INICIAL) return <Simulador />;
  return (
    <>
      <TelaDesconhecida tela={tela} />
      <Simulador />
    </>
  );
}

export function App() {
  const [tema, setTema] = useState(() => temaEscolhido());
  const [apagouRascunho, setApagouRascunho] = useState(false);
  const cabecalho = useRef(null);
  const { tela, parte } = useRota();

  useRolarAoTrocar(`${tela}/${parte ?? ''}`);

  // O menu rola na horizontal (`overflow-x: auto`), e a partir da quarta tela
  // o último item nasce fora da vista num celular de 390 px. Quem abre essa
  // tela vê o próprio nome cortado pela borda — a leitura natural é "esta
  // página não está no menu". Uma linha põe o item atual à vista. O `?.` não
  // é defensivo por hábito: `scrollIntoView` não existe no jsdom dos testes.
  useEffect(() => {
    const atual = document.querySelector('.menu__link--atual');
    atual?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [tela]);

  useEffect(() => {
    setApagouRascunho(conferirVersao());
  }, []);

  /**
   * Publica a altura do cabeçalho para o CSS saber de quanto desviar.
   *
   * O cabeçalho é `sticky`, então ele cobre o topo de qualquer área para onde
   * a página role. Depois de calcular, o simulador rola até o resultado e o
   * selo de confiança parava escondido embaixo dele — defeito que nenhum
   * teste pega, porque em jsdom nada tem altura.
   *
   * A altura é medida e não chutada: o cabeçalho cresce quando o produtor
   * aumenta a letra do celular, e é justamente esse produtor que um valor
   * fixo deixaria sem ver o selo. `scroll-padding-top` no elemento que rola
   * vale para TODO salto da página — `scrollIntoView`, âncora, foco por
   * teclado —, não só para o caso que eu notei olhando.
   */
  useEffect(() => {
    const alvo = cabecalho.current;
    if (!alvo || typeof ResizeObserver === 'undefined') return undefined;
    const medir = () => {
      const altura = Math.ceil(alvo.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--altura-cabecalho', `${altura}px`);
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(alvo);
    return () => observador.disconnect();
  }, []);

  function alternarTema() {
    const novo = proximoTema(tema);
    aplicarTema(novo);
    setTema(novo);
  }

  return (
    <div className="aplicativo">
      <header className="cabecalho" ref={cabecalho}>
        <div className="cabecalho__interno pagina">
          <a className="cabecalho__marca" href={paraRota(TELA_INICIAL)}>
            Biogás
          </a>
          <nav className="menu" aria-label="Telas">
            {MENU.map((m) => (
              <a
                key={m.tela}
                className={`menu__link ${m.tela === tela ? 'menu__link--atual' : ''}`}
                href={paraRota(m.tela)}
                aria-current={m.tela === tela ? 'page' : undefined}
              >
                {m.rotulo}
              </a>
            ))}
          </nav>
          <button
            type="button"
            className="botao botao--texto cabecalho__tema"
            onClick={alternarTema}
            title={NOME_DO_TEMA[tema]}
          >
            <span aria-hidden="true">{SIMBOLO_DO_TEMA[tema] ?? '◐'}</span>
            <span className="so-leitor">{NOME_DO_TEMA[tema] ?? 'Trocar o tema'}</span>
          </button>
        </div>
      </header>

      <main className="pagina">
        {apagouRascunho ? (
          <p className="aviso-de-cache" role="status">
            O app foi atualizado e os dados guardados no aparelho foram limpos. Se
            você tinha uma simulação em andamento, refaça — leva menos de um minuto.
          </p>
        ) : null}

        <Conteudo tela={tela} parte={parte} />
      </main>

      <RodapeDaTela tela={tela} />
    </div>
  );
}
